import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ToolTipPortal from "components/ToolTipPortal/ToolTipPortal";
import React, { useMemo, useRef, useState } from "react";
import { FormatMoneyNumber } from "utils/numberUtils";
import { RecievedPayments } from "./RecievedPayments";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import CancelIcon from "@mui/icons-material/Cancel";
import { grey } from "@mui/material/colors";
import { useSelector } from "react-redux";
import {
  selectState,
  setIndexForDeletionAccountReceivable,
  setNote,
  setIndexForPaymentCreation,
  setInvoicePopover,
  setIndexForInvoice,
  setPaymentCreationPopover,
  updateAccountReceivable,
  updateAccountReceivableTemporarily,
  updateReceivablePlanTemporarily,
} from "../accountReceivable.slice";
import { useAppDispatch } from "hooks/hooks";
import { partnersService } from "Dashboard/scenes/Partners/services/partner.service";
import InfoTooltip from "components/Popup/InfoTooltip";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { IAccountReceivablesWithPayments, IUpdateAccountReceivableRequestParams } from "Dashboard/scenes/Partners/domain";
import { FormatDateIso } from "utils/dateUtils";
import VatSelect from "Dashboard/components/VatSelect";
import customStyles from "../../../styles/SelectStyle";
import NumberInput from "Dashboard/components/NumberInput";
import ProjectSelectForReceivable from "Dashboard/scenes/Projects/components/ProjectSelectForReceivable";
import { styles as dropdownStyles } from "Dashboard/components/SelectStyle";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { getAllReceivablePlans, updatePartnerPlanReceivable } from "../../Main/main.slice";
import { debouncePerRow } from "utils/debounceUtil";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { PARTNER_MODULE_NAME } from "Dashboard/scenes/Partners/domain/PartnerModule";
import { IProjectOption } from "Dashboard/domain/IProjectOption";
import { useWindowedRows } from "hooks/useWindowedRows";


interface IProps {
  projects: IProjectOption[];
};

const initialState = {
  isOpen: false,
  top: 0,
  left: 0,
};

// Per-row footprint in px: the `.partners-paying .left/.right ... .row` rule
// sets height 16px + margin-bottom 10px. Keep in sync with that CSS.
const ROW_HEIGHT = 26;

export const TableBody = ({ projects }: IProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { accountReceivablesWithPayments } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const [noteTooltip, setNoteTooltip] = useState(initialState);
  const [selectedNote, setSelectedNote] = useState("");
  const [selectedRecievableId, setSelectedRecievableId] = useState<number>();

  const list = accountReceivablesWithPayments ?? [];

  // Each account renders one main row plus one row per received payment, so
  // items have variable height.
  const itemHeights = useMemo(
    () => list.map((item) => (1 + item.receivedPayments.length) * ROW_HEIGHT),
    [list]
  );

  const { contentRef, startIndex, endIndex, topSpacer, bottomSpacer } =
    useWindowedRows({ count: list.length, itemHeights });

  const close = () => {
    setNoteTooltip(initialState);
    if (selectedNote !== "" && selectedRecievableId) {
      partnersService
        .updateAccountReceivable({
          accountReceivableId: selectedRecievableId,
          note: selectedNote,
        })
        .then(() => {
          dispatch(
            setNote({ receivableId: selectedRecievableId, note: selectedNote })
          );
        });
    }
  };

  const handleInfoIconClick = (
    note: string,
    invoiceId: number,
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    setSelectedNote(note);
    setSelectedRecievableId(invoiceId);
    setNoteTooltip({
      isOpen: true,
      top: e.clientY,
      left: e.clientX,
    });
  };

  const handlePlanAccountReceivable = async (
    partnerId: number,
    accountReceivableId: number,
    planned: boolean
  ) => {
    const data = {
      partnerId: partnerId,
      accountReceivableId: accountReceivableId,
      planned: planned,
    };
    await dispatch(updatePartnerPlanReceivable(data));
    dispatch(updateReceivablePlanTemporarily(data));
    dispatch(getAllReceivablePlans());
  };

  const onChangeBasicField = (data: IUpdateAccountReceivableRequestParams) => {
    dispatch(updateAccountReceivableTemporarily(data));
    updateFieldWithDelay(data);
  };

  const updateFieldWithDelay = useMemo(
    () => debouncePerRow(async (data: IUpdateAccountReceivableRequestParams) => {
      try {
        await dispatch(updateAccountReceivable(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000, "accountReceivableId"),
    [dispatch]
  );

  const getInputField = (item: IAccountReceivablesWithPayments) => {
    return (<NumberInput
      formatAs="money"
      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
        if (parsedValue !== item.accountReceivable.amountInvoiced) {
          onChangeBasicField({
            accountReceivableId: item.accountReceivable.receivableId,
            amountInvoiced: parsedValue
          });
        }
      }}
      value={`${item.accountReceivable.amountInvoiced}`}
      className={`${item.accountReceivable.isAdvance ? 'center bg-safe' : 'center'}`}
      disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
    />);
  }

  return (
    <div ref={tableRef} className="table-body">
      <div className="g-grid g-content" ref={contentRef}>
        {topSpacer > 0 && <div style={{ height: topSpacer }} aria-hidden />}
        {list
          .slice(startIndex, endIndex)
          .map((item, offset) => {
            const index = startIndex + offset;
            return (
              <React.Fragment key={item.accountReceivable.receivableId}>
                <div className="row">
                  <div className="col center">
                    {hasFullAccessTo(user, PARTNER_MODULE_NAME)
                      ? <InfoTooltip tooltip="Obriši zapis" classesProp="btn delete-btn" element="span">
                        <button
                          className="btn delete-btn"
                          onClick={() => {
                            dispatch(setIndexForDeletionAccountReceivable(index));
                          }}
                        >
                          <CancelIcon
                            sx={{ color: grey[300] }}
                            fontSize="small"
                          />
                        </button>
                      </InfoTooltip>
                      : <></>}
                  </div>
                  <div className="col center">
                    <input type="text" className="bg-item center light"
                      onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                        ? (e) => {
                          onChangeBasicField({
                            accountReceivableId: item.accountReceivable.receivableId,
                            invoiceId: e.target.value
                          });
                        }
                        : undefined}
                      value={item.accountReceivable.invoiceId}
                      disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                    />
                  </div>
                  <div className="col">
                    <div className="projSelectContainer col">
                      {item.project.active && projects && hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                        ? <ProjectSelectForReceivable
                          preloadedOptions={projects}
                          onChange={(value: number) => {
                            onChangeBasicField({
                              accountReceivableId: item.accountReceivable.receivableId,
                              projectId: value
                            })
                          }}
                          customStyles={customStyles}
                          defaultValue={item.project.projectId}
                          scrollContainerRef={tableRef}
                        />
                        : <input type="text" style={{ fontSize: "10px" }}
                          className="bg-item-dark" value={item.project.name}
                          readOnly />}
                      <span
                        className="note-wrapper"
                        onClick={(e) =>
                          handleInfoIconClick(
                            item.accountReceivable.note,
                            item.accountReceivable.receivableId,
                            e
                          )
                        }
                      >
                        <BsFillQuestionCircleFill className="question" />
                      </span>
                    </div>
                  </div>
                  <div className="col center">
                    <div className="bg-item">
                      <DatePicker
                        popperProps={{ strategy: "fixed" }}
                        className="date-picker light"
                        showMonthDropdown
                        showYearDropdown
                        onChange={(date) => {
                          if (date) {
                            onChangeBasicField({
                              accountReceivableId: item.accountReceivable.receivableId,
                              invoiceDate: FormatDateIso(date)
                            });
                          }
                        }}
                        selected={item.accountReceivable.invoiceDate
                          ? new Date(item.accountReceivable.invoiceDate) : null}
                        dateFormat="dd.MM.yy"
                        disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                      />
                    </div>
                  </div>
                  <div className="col center">
                    <div className="bg-item">
                      <DatePicker
                        popperProps={{ strategy: "fixed" }}
                        className="date-picker light"
                        showMonthDropdown
                        showYearDropdown
                        onChange={(date) => {
                          if (date) {
                            onChangeBasicField({
                              accountReceivableId: item.accountReceivable.receivableId,
                              dueDate: FormatDateIso(date)
                            });
                          }
                        }}
                        selected={item.accountReceivable.dueDate
                          ? new Date(item.accountReceivable.dueDate) : null}
                        dateFormat="dd.MM.yy"
                        disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                      />
                    </div>
                  </div>
                  {item.accountReceivable.isAdvance
                    ? <InfoTooltip tooltip="Avans" classesProp="col center" element="div">
                      {getInputField(item)}
                    </InfoTooltip>
                    : <div className="col center">
                      {getInputField(item)}
                    </div>
                  }
                  <div className="col center">
                    <div className="bg-item center">
                      <span className="strong important">
                        {FormatMoneyNumber(
                          item.accountReceivable.amountInvoiced -
                          item.accountReceivable.amountPaid
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="col center">
                    <div className="bg-item center">
                      <span className="strong">
                        {FormatMoneyNumber(item.accountReceivable.amountPaid)}
                      </span>
                    </div>
                  </div>
                  <div className="col center">
                    <InfoTooltip tooltip="Račun" classesProp="btn file-btn" element="span">
                      <button
                        className="btn file-btn"
                        onClick={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                          ? (e) => {
                            dispatch(
                              setInvoicePopover({
                                isOpen: true,
                                top: e.clientY + 5,
                                left: e.clientX,
                                offsetTop: -285,
                                offsetLeft: 25
                              })
                            );
                            dispatch(setIndexForInvoice(item.accountReceivable.receivableId));
                          }
                          : undefined}
                        disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                      >
                        <FontAwesomeIcon icon={faFile} className="icon" />
                      </button>
                    </InfoTooltip>
                  </div>
                  <div className="col center">
                    <div className="bg-item vat center">
                      <VatSelect
                        customStyles={dropdownStyles.dropdownInTable}
                        value={item.accountReceivable.vatRate}
                        onChange={(vatRate: number) => {
                          onChangeBasicField({
                            accountReceivableId: item.accountReceivable.receivableId,
                            vatRate: vatRate
                          });
                        }}
                        isDisabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                      />
                    </div>
                  </div>
                  <div className="col center">
                    {item.accountReceivable.amountInvoiced -
                      item.accountReceivable.amountPaid !== 0
                      && hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                      && (
                        <InfoTooltip tooltip="Plan" classesProp="planCheckbox">
                          <input
                            type="checkbox"
                            checked={item.planned}
                            onChange={(event) => handlePlanAccountReceivable(
                              item.accountReceivable.partnerId,
                              item.accountReceivable.receivableId,
                              event.target.checked
                            )}
                          />
                        </InfoTooltip>
                      )}
                  </div>
                  <div className="col center">
                    <InfoTooltip tooltip="Nova uplata" classesProp="btn plus-btn" element="span">
                      <button
                        className="btn plus-btn"
                        onClick={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                          ? (e) => {
                            dispatch(setIndexForPaymentCreation(index));
                            dispatch(
                              setPaymentCreationPopover({
                                isOpen: true,
                                left: e.clientX,
                                top: e.clientY + 5,
                              })
                            );
                          }
                          : undefined}
                        disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
                      >
                        <FontAwesomeIcon icon={faPlusCircle} className="icon" />
                      </button>
                    </InfoTooltip>
                  </div>
                </div>
                <RecievedPayments item={item} accountIndex={index} />
              </React.Fragment>
            );
          })}
        {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} aria-hidden />}
      </div>
      <ToolTipPortal
        {...noteTooltip}
        onClose={close}
        text="Napomena"
        offset={{ top: -46, left: 10 }}
      >
        <textarea
          onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME) ? (e) => setSelectedNote(e.target.value) : undefined}
          disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}>
          {selectedNote}
        </textarea>
      </ToolTipPortal>
    </div>
  );
};
